import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { 
  MessageSquare, Pin, Bell, Calendar, User, Check, 
  ChevronLeft, Send, AlertCircle
} from 'lucide-react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import './EmployeeInbox.css';

function EmployeeInbox({ employeeId, onBack }) {
  const { 
    state, 
    getEmployeeInternalMessages,
    markInternalMessageRead,
    acknowledgeInternalMessage,
    replyToInternalMessage,
    getEmployeeById
  } = useApp();

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const employee = getEmployeeById(employeeId);
  const messages = getEmployeeInternalMessages(employeeId);

  // Filter messages
  const filteredMessages = useMemo(() => {
    switch (filter) {
      case 'announcements':
        return messages.filter(m => m.type === 'announcement');
      case 'direct':
        return messages.filter(m => m.type === 'direct');
      case 'schedule':
        return messages.filter(m => m.type === 'schedule_update');
      case 'unread':
        return messages.filter(m => !m.readBy?.includes(employeeId));
      default:
        return messages;
    }
  }, [messages, filter, employeeId]);

  // Separate pinned and unpinned
  const pinnedMessages = filteredMessages.filter(m => m.isPinned);
  const unpinnedMessages = filteredMessages.filter(m => !m.isPinned);

  // Handle message selection
  const handleSelectMessage = async (message) => {
    setSelectedMessage(message);
    setReplyText('');
    
    // Mark as read
    if (!message.readBy?.includes(employeeId)) {
      await markInternalMessageRead(message.id, employeeId);
    }
  };

  // Handle acknowledge
  const handleAcknowledge = async () => {
    if (!selectedMessage) return;
    await acknowledgeInternalMessage(selectedMessage.id, employeeId);
  };

  // Handle reply
  const handleSendReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;
    
    setSending(true);
    try {
      await replyToInternalMessage(selectedMessage.id, {
        fromId: employeeId,
        fromName: employee ? `${employee.firstName} ${employee.lastName || ''}`.trim() : 'Employee',
        body: replyText
      });
      setReplyText('');
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  // Get unread count
  const unreadCount = messages.filter(m => !m.readBy?.includes(employeeId)).length;

  // Message type icon
  const getMessageIcon = (type) => {
    switch (type) {
      case 'announcement': return <Bell size={16} />;
      case 'schedule_update': return <Calendar size={16} />;
      default: return <User size={16} />;
    }
  };

  // Message type label
  const getTypeLabel = (type) => {
    switch (type) {
      case 'announcement': return 'Announcement';
      case 'schedule_update': return 'Schedule Update';
      case 'direct': return 'Direct Message';
      default: return 'Message';
    }
  };

  // If viewing a specific message
  if (selectedMessage) {
    const isRead = selectedMessage.readBy?.includes(employeeId);
    const isAcknowledged = selectedMessage.acknowledgedBy?.includes(employeeId);
    const needsAcknowledgment = selectedMessage.requiresAcknowledgment && !isAcknowledged;
    const acknowledgeCount = selectedMessage.acknowledgedBy?.length || 0;
    
    return (
      <div className="employee-inbox message-view">
        <div className="inbox-header">
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedMessage(null)}>
            <ChevronLeft size={18} /> Back
          </button>
        </div>

        <div className="message-detail">
          <div className="message-detail-header">
            <div className="message-type-badge" data-type={selectedMessage.type}>
              {getMessageIcon(selectedMessage.type)}
              {getTypeLabel(selectedMessage.type)}
            </div>
            {selectedMessage.isPinned && (
              <span className="pinned-badge"><Pin size={14} /> Pinned</span>
            )}
          </div>

          <h2>{selectedMessage.subject}</h2>
          
          <div className="message-meta">
            <span>From: <strong>{selectedMessage.fromName}</strong></span>
            <span>{format(parseISO(selectedMessage.createdAt), 'MMM d, yyyy \'at\' h:mm a')}</span>
          </div>

          <div className="message-body">
            {selectedMessage.body.split('\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
          </div>

          {/* Acknowledgment section */}
          {selectedMessage.requiresAcknowledgment && (
            <div className={`acknowledge-section ${isAcknowledged ? 'acknowledged' : ''}`}>
              {isAcknowledged ? (
                <div className="acknowledge-status">
                  <Check size={20} />
                  <span>You acknowledged this message</span>
                </div>
              ) : (
                <>
                  <div className="acknowledge-prompt">
                    <AlertCircle size={20} />
                    <span>Please acknowledge that you've read this message</span>
                  </div>
                  <button className="btn btn-primary" onClick={handleAcknowledge}>
                    <Check size={16} /> I Acknowledge
                  </button>
                </>
              )}
            </div>
          )}

          {/* Reply section (only for direct messages or if not an announcement) */}
          {selectedMessage.type !== 'announcement' && selectedMessage.fromId !== 'system' && (
            <div className="reply-section">
              <h4>Reply</h4>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                rows={3}
              />
              <button 
                className="btn btn-primary"
                onClick={handleSendReply}
                disabled={!replyText.trim() || sending}
              >
                {sending ? 'Sending...' : <><Send size={16} /> Send Reply</>}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main inbox view
  return (
    <div className="employee-inbox">
      <div className="inbox-header">
        {onBack && (
          <button className="btn btn-secondary btn-sm" onClick={onBack}>
            <ChevronLeft size={18} /> Back
          </button>
        )}
        <h2><MessageSquare size={24} /> Messages</h2>
        {unreadCount > 0 && (
          <span className="unread-badge">{unreadCount} unread</span>
        )}
      </div>

      {/* Filters */}
      <div className="inbox-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button 
          className={`filter-btn ${filter === 'announcements' ? 'active' : ''}`}
          onClick={() => setFilter('announcements')}
        >
          📢 Announcements
        </button>
        <button 
          className={`filter-btn ${filter === 'direct' ? 'active' : ''}`}
          onClick={() => setFilter('direct')}
        >
          👤 Direct
        </button>
        <button 
          className={`filter-btn ${filter === 'schedule' ? 'active' : ''}`}
          onClick={() => setFilter('schedule')}
        >
          📅 Schedule
        </button>
        {unreadCount > 0 && (
          <button 
            className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            Unread ({unreadCount})
          </button>
        )}
      </div>

      {/* Message List */}
      <div className="inbox-messages">
        {filteredMessages.length === 0 ? (
          <div className="empty-inbox">
            <MessageSquare size={48} />
            <h3>No messages</h3>
            <p>You're all caught up!</p>
          </div>
        ) : (
          <>
            {/* Pinned Messages */}
            {pinnedMessages.length > 0 && (
              <div className="pinned-section">
                {pinnedMessages.map(message => (
                  <MessageItem 
                    key={message.id}
                    message={message}
                    employeeId={employeeId}
                    onClick={() => handleSelectMessage(message)}
                    getMessageIcon={getMessageIcon}
                  />
                ))}
              </div>
            )}

            {/* Regular Messages */}
            {unpinnedMessages.map(message => (
              <MessageItem 
                key={message.id}
                message={message}
                employeeId={employeeId}
                onClick={() => handleSelectMessage(message)}
                getMessageIcon={getMessageIcon}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// Message Item Component
function MessageItem({ message, employeeId, onClick, getMessageIcon }) {
  const isRead = message.readBy?.includes(employeeId);
  const needsAcknowledgment = message.requiresAcknowledgment && !message.acknowledgedBy?.includes(employeeId);

  return (
    <div 
      className={`message-item ${message.isPinned ? 'pinned' : ''} ${!isRead ? 'unread' : ''}`}
      onClick={onClick}
    >
      <div className="message-item-header">
        <span className="message-from">
          {message.isPinned && <Pin size={14} className="pin-icon" />}
          {!isRead && <span className="unread-dot" />}
          {message.fromName}
        </span>
        <span className="message-time">
          {formatDistanceToNow(parseISO(message.createdAt), { addSuffix: true })}
        </span>
      </div>
      <div className="message-subject">
        {getMessageIcon(message.type)}
        {message.subject}
      </div>
      <div className="message-preview">
        {message.body.substring(0, 100)}{message.body.length > 100 ? '...' : ''}
      </div>
      {needsAcknowledgment && (
        <div className="needs-acknowledgment">
          <AlertCircle size={14} /> Requires acknowledgment
        </div>
      )}
    </div>
  );
}

export default EmployeeInbox;
